import React, { useEffect, useRef, useState } from 'react';

const GATHER_STAGGER_S = 0.38;
const SPREAD_STAGGER_S = 0.36;
const GATHER_HOLD_S = 1.35;
const GATHER_FADE_S = 0.95;

const WHY_CHOOSE_ITEMS = [
  {
    number: '01',
    title: 'Verified Listings',
    text:
      'Every property on our platform is verified for authenticity, ensuring you get accurate information and genuine listings.',
  },
  {
    number: '02',
    title: 'Transparent Pricing',
    text:
      'No hidden costs or surprises. We provide clear, upfront pricing information for all properties and services.',
  },
  {
    number: '03',
    title: 'Expert Support',
    text:
      'Our team of real estate experts is always ready to assist you with property search, negotiations, and transaction support.',
  },
  {
    number: '04',
    title: 'Wide Selection',
    text:
      'Browse through thousands of properties across India - from apartments and villas to commercial spaces and plots.',
  },
  {
    number: '05',
    title: 'Advanced Search',
    text:
      'Use our powerful search filters to find properties that match your exact requirements - location, price, size, and more.',
  },
  {
    number: '06',
    title: 'Secure Transactions',
    text:
      'We prioritize your security and privacy, ensuring safe and secure property transactions with proper documentation support.',
  },
];

const WhyChooseSection = () => {
  const sectionRef = useRef(null);
  const gridRef = useRef(null);
  const itemRefs = useRef([]);
  const [isActive, setIsActive] = useState(false);
  const [offsets, setOffsets] = useState([]);
  const [animationPhase, setAnimationPhase] = useState('idle');

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) {
      setIsActive(true);
      return undefined;
    }

    let done = false;
    const activate = () => {
      if (done) return;
      done = true;
      setIsActive(true);
    };

    const isInView = () => {
      const rect = section.getBoundingClientRect();
      return rect.top < window.innerHeight * 0.88 && rect.bottom > 0;
    };

    if (isInView()) {
      activate();
      return undefined;
    }

    const fallback = window.setTimeout(activate, 700);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) activate();
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(section);

    const onScroll = () => {
      if (isInView()) {
        activate();
        observer.disconnect();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.clearTimeout(fallback);
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    if (!isActive || animationPhase !== 'idle') {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setAnimationPhase('placed');
      return undefined;
    }

    const measureAndGather = () => {
      const grid = gridRef.current;
      if (!grid) return;

      const gridRect = grid.getBoundingClientRect();
      const centerX = gridRect.width / 2;
      const centerY = gridRect.height / 2;

      const measuredOffsets = itemRefs.current.map((el) => {
        if (!el) return { x: 0, y: 0 };
        const rect = el.getBoundingClientRect();
        const itemCenterX = rect.left - gridRect.left + rect.width / 2;
        const itemCenterY = rect.top - gridRect.top + rect.height / 2;
        return {
          x: centerX - itemCenterX,
          y: centerY - itemCenterY,
        };
      });

      setOffsets(measuredOffsets);
      requestAnimationFrame(() => setAnimationPhase('gathered'));
    };

    requestAnimationFrame(() => requestAnimationFrame(measureAndGather));

    return undefined;
  }, [isActive, animationPhase]);

  useEffect(() => {
    if (animationPhase !== 'gathered') {
      return undefined;
    }

    const spreadDelayMs =
      ((WHY_CHOOSE_ITEMS.length - 1) * GATHER_STAGGER_S + GATHER_FADE_S + GATHER_HOLD_S) *
      1000;

    const timer = window.setTimeout(() => setAnimationPhase('placed'), spreadDelayMs);

    return () => window.clearTimeout(timer);
  }, [animationPhase]);

  const isReady = offsets.length === WHY_CHOOSE_ITEMS.length;
  const sectionClass = [
    'about-section',
    'about-why-choose',
    isActive ? 'is-active' : '',
    isReady ? 'wc-ready' : '',
    animationPhase !== 'idle' ? `wc-phase-${animationPhase}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section ref={sectionRef} className={sectionClass} aria-labelledby="why-choose-heading">
      <div className="about-container">
        <div className="about-section-header">
          <h2 id="why-choose-heading" className="about-section-title">
            Why Choose 360Coordinates?
          </h2>
          <div className="about-title-underline" aria-hidden="true" />
        </div>

        <div ref={gridRef} className="why-choose-grid">
          {WHY_CHOOSE_ITEMS.map((item, index) => {
            const offset = offsets[index] || { x: 0, y: 0 };
            return (
              <div
                key={item.number}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                className={`why-choose-item wc-item wc-${animationPhase}`}
                style={{
                  '--wc-offset-x': `${offset.x}px`,
                  '--wc-offset-y': `${offset.y}px`,
                  '--wc-gather-delay': `${index * GATHER_STAGGER_S}s`,
                  '--wc-spread-delay': `${index * SPREAD_STAGGER_S}s`,
                  '--wc-index': index,
                }}
              >
                <div className="why-choose-number">{item.number}</div>
                <h3 className="why-choose-title">{item.title}</h3>
                <p className="why-choose-text">{item.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
